import {useEffect} from 'react';
import {useNavigate, useParams} from 'react-router';

import {profileDetailsTabListTabT} from '@/lib/types/form.types';

import {useItemQuery} from '@/hooks/useItemQuery';

import {useAuth} from '@/providers/components/authProvider';

import {
  LoadingSpinningLogoComponent,
  ProfileCardComponent,
  ProfileDetailsTabListComponent,
  SpeculatorDetailOrdersComponent,
  SpeculatorDetailPositionsComponent,
  profileCardHeaderT,
  profileCardSectionT,
} from '@/components';
import Page from '@/components/Page';

function SpeculatorDetails() {
  const {isAdministrator, isAdmin, isSuperAdmin} = useAuth();
  const navigate = useNavigate();
  const {id} = useParams();

  // Tanstack Queries
  const {
    data: speculator,
    error,
    isLoading,
    isFetching,
  } = useItemQuery({
    table: 'Speculator',
    id: Number(id),
    enabled: !!id,
  });

  // Reactivity
  useEffect(() => {
    if (error || !id) {
      console.error(error ?? 'Error getting details');
      navigate('/admin/speculators');
    }
  }, [error, id, navigate]);

  const headerInfo: profileCardHeaderT = {
    subHeader: `Speculator${isAdministrator ? ' | ' : null}${isAdmin ? 'Admin' : ''} ${isSuperAdmin ? ' | Super Admin' : ''}`,
    perks: [
      {icon: 'ri-mail-line', content: speculator?.speculator_email || ''},
      {
        icon: 'ri-map-pin-line',
        content: `${speculator?.speculator_city || ''}, ${speculator?.speculator_state?.toUpperCase() || ''}`,
      },
    ],
    stats: [
      {number: 155, title: 'Total Positions'},
      {number: 122, title: 'Active Positions'},
    ],
  };

  const sections: profileCardSectionT[] = [
    {
      title: 'Account Information',
      items: [
        {header: 'First Name', content: speculator?.speculator_name_first || ''},
        {header: 'Last Name', content: speculator?.speculator_name_last || ''},
        {header: 'Username', content: speculator?.speculator_username || ''},
        {header: 'Date of Birth', content: speculator?.speculator_dob || ''},
        {header: 'Date Created', content: speculator?.date_created},
        {header: 'Date Updated', content: speculator?.date_modified},
      ],
    },
    {
      title: 'Contact Information',
      items: [
        {header: 'Email', content: speculator?.speculator_email},
        {header: 'Phone', content: speculator?.speculator_phone || ''},
        {
          header: 'Address',
          content: `${speculator?.speculator_address}, ${speculator?.speculator_city}, ${speculator?.speculator_state?.toUpperCase()}, ${speculator?.speculator_country}, ${speculator?.speculator_postal_code}`,
        },
      ],
    },
  ];

  const tabs: profileDetailsTabListTabT[] = [
    {
      id: 'positions',
      title: 'Positions',
      icon: 'ri-shopping-cart-2-line',
      content: speculator && <SpeculatorDetailPositionsComponent speculator={speculator} />,
    },
    {
      id: 'orders',
      title: 'Orders',
      icon: 'ri-file-list-2-line',
      content: speculator && <SpeculatorDetailOrdersComponent />,
    },
    {id: 'sales', title: 'Sales', icon: 'ri-shopping-bag-2-line', content: <h1>sales tag</h1>},
    {id: 'returns', title: 'Returns', icon: 'ri-refund-2-line', content: <h1>returns tag</h1>},
    {id: 'deposits', title: 'Deposits', icon: 'ri-bank-line', content: <h1>deposits tag</h1>},
    {id: 'withdrawals', title: 'Withdrawals', icon: 'ri-bank-card-line', content: <h1>withdrawals tag</h1>},
  ];

  return (
    <Page currentPage="Speculator">
      <div className="grid grid-cols-12 gap-x-6">
        {isLoading || isFetching ? (
          // TODO: Add skeleton loader
          <LoadingSpinningLogoComponent />
        ) : (
          <>
            <ProfileCardComponent
              avatar={'https://placekeanu.com/100/100'}
              name={`${speculator?.speculator_name_first} ${speculator?.speculator_name_last} ` || ''}
              headerInfo={headerInfo}
              sections={sections}
            />
            <ProfileDetailsTabListComponent tabs={tabs} />
          </>
        )}
      </div>
    </Page>
  );
}

export default SpeculatorDetails;
